import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useRef, useMemo, } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import PageContainer from '../common_components/PageContainer';
import TableToolbar from '../common_components/TableToolbar';
import Pagination from '../common_components/Pagination';
import AppButton from '../common_components/AppButton';
import HolidayDrawer from '../common_components/HolidayDrawer';
import '../App.css';
import CommonTable from '../common_components/CommonTable';
import CommonTabs from '../common_components/CommonTabs';
import { ApiCall } from '../helper/axios';
import { useSelector } from 'react-redux';
import { selectPermissions, selectToken } from '../redux/authSlice';
import useToast from '../common_components/Toast/useToast';
import { CircularProgress } from '@mui/material';
import ConfirmActionModal from '../common_components/ConfirmActionModal';
const roleClassSequence = ['role-badge-1', 'role-badge-2', 'role-badge-3'];
function mapHolidaysFromApi(holidays = []) {
    return (holidays || []).map((h) => ({
        id: h?._id,
        name: h?.name || '',
        ordering: h?.ordering,
        start_date: h?.start_date || '',
        end_date: h?.end_date || '',
        prev_start_date: h?.prev_start_date || null,
        prev_end_date: h?.prev_end_date || null,
        created_at: h?.createdAt || '',
        status: h?.status || 'active',
        raw: h,
    }));
}
export default function HolidaysSettings() {
    const toast = useToast();
    const token = useSelector(selectToken);
    const selectPermission = useSelector(selectPermissions);
    const [activeTab, setActiveTab] = useState('active');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({
        columnId: 'created_at',
        direction: 'desc',
    });
    const [searchText, setSearchText] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedHoliday, setSelectedHoliday] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const isInitialMount = useRef(true);
    const [activeCount, setActiveCount] = useState(0);
    const [inactiveCount, setInactiveCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [editLoadingId, setEditLoadingId] = useState(null);
    const [deleteState, setDeleteState] = useState({
        open: false,
        loading: false,
        payload: null,
    });
    // New state for table features
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [columnFilters, setColumnFilters] = useState({});
    const [columnOrder, setColumnOrder] = useState(null);
    const [showFilterRow, setShowFilterRow] = useState(false);
    const [activeFilterColumnId, setActiveFilterColumnId] = useState(null);
    const [pinnedColumns, setPinnedColumns] = useState({
        left: [],
        right: [],
    });
    const [totalHolidaysCount, setTotalHolidaysCount] = useState(0);
    const hasPermission = selectPermission?.holiday_calendar?.actions?.includes('Manage Holidays') ?? false;
    const itemsPerPage = 20;
    const fetchHolidays = useCallback(async () => {
        try {
            setLoading(true);
            const status = activeTab === 'active' ? 'active' : 'inactive';
            const params = new URLSearchParams();
            params.set('page', String(currentPage));
            params.set('limit', String(itemsPerPage));
            if (searchText)
                params.set('searchText', searchText);
            params.set('status', status);
            const sortByMap = {
                created_at: 'createdAt',
                name: 'name',
                cur_promotion_date_range: 'start_date',
                prev_promotion_date_range: 'prev_start_date',
            };
            if (sortConfig?.columnId && sortConfig.direction) {
                const apiSortBy = sortByMap[sortConfig.columnId] || 'created_at';
                params.set('sortBy', apiSortBy);
                params.set('sortOrder', sortConfig.direction);
            }
            // Add column filters as params
            Object.keys(columnFilters).forEach((columnId) => {
                const value = columnFilters[columnId];
                if (value) {
                    if (columnId === 'name') {
                        params.set('searchText', value);
                    }
                }
            });
            const res = await ApiCall('GET', `/api/holidays?${params.toString()}`, '', { Authorization: token });
            if (res && 'data' in res && res?.data?.success) {
                const payload = res.data?.data || {};
                const list = mapHolidaysFromApi(payload?.holidays || []);
                setRows(list);
                const pagination = payload?.pagination || {};
                // Get total counts for active and inactive
                setActiveCount(pagination?.active || 0);
                setInactiveCount(pagination?.inactive || 0);
                setTotalHolidaysCount(pagination?.total || 0);
                setTotalPages(Number(pagination?.totalPages || 1));
            }
            else {
                setRows([]);
            }
        }
        catch (e) {
            setRows([]);
        }
        finally {
            setLoading(false);
        }
    }, [
        activeTab,
        currentPage,
        sortConfig,
        token,
        itemsPerPage,
        searchText,
        columnFilters,
    ]);
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchHolidays();
        }, 400);
        return () => clearTimeout(timer);
    }, [fetchHolidays, searchText, columnFilters, currentPage, activeTab, sortConfig]);
    const handleSearchChange = (text) => {
        setSearchText(text);
    };
    const handleSelectionChange = (newSelected) => {
        setSelectedRows(newSelected);
    };
    const handleColumnVisibilityChange = (newHidden) => {
        setHiddenColumns(newHidden);
    };
    const handleColumnVisibilityClick = (event) => {
        setColumnVisibilityAnchor(event.currentTarget);
    };
    const handleColumnVisibilityClose = () => {
        setColumnVisibilityAnchor(null);
    };
    const handleColumnFilterChange = (columnId, filterValue) => {
        setColumnFilters((prev) => {
            const newFilters = { ...prev };
            if (filterValue) {
                newFilters[columnId] = filterValue;
            }
            else {
                delete newFilters[columnId];
            }
            return newFilters;
        });
        setCurrentPage(1);
    };
    const handleColumnOrderChange = (newOrder) => {
        setColumnOrder(newOrder);
    };
    const handleColumnPinningChange = (newPinnedColumns) => {
        setPinnedColumns(newPinnedColumns);
    };
    const handleFilterRowToggle = (payload = null) => {
        if (!payload || payload?.mode === 'all') {
            setActiveFilterColumnId(null);
            setShowFilterRow((prev) => payload?.open !== undefined ? payload.open : !prev);
            return;
        }
        if (payload?.mode === 'single' && payload?.columnId) {
            if (payload?.open === false) {
                setActiveFilterColumnId(null);
                setShowFilterRow(false);
                return;
            }
            setActiveFilterColumnId(payload.columnId);
            setShowFilterRow(true);
        }
    };
    const handleSortChange = (columnId, direction) => {
        if (!columnId || !direction) {
            setSortConfig({ columnId: null, direction: 'asc' });
        }
        else {
            setSortConfig({ columnId, direction });
        }
        setCurrentPage(1);
    };
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };
    const handleEdit = async (holiday) => {
        try {
            setEditLoadingId(holiday.id);
            const res = await ApiCall('GET', `/api/holidays/${holiday.id}`, '', {
                Authorization: token,
            });
            if (res && 'data' in res && res?.data?.success) {
                const fetchedHoliday = res.data?.data || {};
                const holidayData = {
                    id: fetchedHoliday._id,
                    name: fetchedHoliday.name || '',
                    ordering: fetchedHoliday.ordering,
                    start_date: fetchedHoliday.start_date || '',
                    end_date: fetchedHoliday.end_date || '',
                    prev_start_date: fetchedHoliday.prev_start_date || '',
                    prev_end_date: fetchedHoliday.prev_end_date || '',
                    status: fetchedHoliday.status || 'active',
                };
                setSelectedHoliday(holidayData);
                setDrawerOpen(true);
            }
            else {
                const errorRes = res;
                toast.error(errorRes?.response?.data?.message || 'Something went wrong');
            }
        }
        catch (error) {
            toast.error('An unexpected error occurred. Please try again.');
        }
        finally {
            setEditLoadingId(null);
        }
    };
    const handleDelete = (row) => {
        setDeleteState({
            open: true,
            loading: false,
            payload: row,
        });
    };
    const confirmDelete = async () => {
        const holiday = deleteState.payload;
        if (!holiday)
            return;
        setDeleteState((p) => ({ ...p, loading: true }));
        try {
            const res = await ApiCall('DELETE', `/api/holidays/${holiday.id}`, null, {
                Authorization: token,
            });
            if (res && 'data' in res && res?.data?.success) {
                toast.success('Holiday deleted successfully');
                setDeleteState({ open: false, loading: false, payload: null });
                fetchHolidays();
            }
            else {
                const errorRes = res;
                toast.error(errorRes?.response?.data?.message || 'Delete failed');
                setDeleteState((p) => ({ ...p, loading: false }));
            }
        }
        catch (error) {
            toast.error('An unexpected error occurred. Please try again.');
            setDeleteState((p) => ({ ...p, loading: false }));
        }
    };
    const handleAddNewHoliday = () => {
        setSelectedHoliday(null);
        setDrawerOpen(true);
    };
    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setSelectedHoliday(null);
    };
    const handleSaveHoliday = async (values) => {
        try {
            const payload = {
                name: values?.name || '',
                ordering: values?.ordering,
                start_date: values?.start_date || '',
                end_date: values?.end_date || '',
                prev_start_date: values?.prev_start_date || null,
                prev_end_date: values?.prev_end_date || null,
                status: values?.status || 'active',
            };
            if (selectedHoliday) {
                const res = await ApiCall('PUT', `/api/holidays/${selectedHoliday.id}`, payload, { Authorization: token });
                if (res && 'data' in res && res?.data?.success) {
                    toast.success('Holiday updated successfully');
                    setDrawerOpen(false);
                    setSelectedHoliday(null);
                    fetchHolidays();
                    return true;
                }
                else {
                    const errorRes = res;
                    const errorMessage = errorRes?.response?.data?.message || 'Update failed';
                    toast.error(errorMessage);
                    // Return error object for formik to display
                    if (errorMessage.includes('End date cannot be before start date')) {
                        return { errors: { end_date: errorMessage } };
                    }
                    else if (errorMessage.includes('Previous end date cannot be before previous start date')) {
                        return { errors: { prev_end_date: errorMessage } };
                    }
                    else if (errorMessage.includes('Current year and previous year cannot be the same')) {
                        return { errors: { prev_start_date: errorMessage } };
                    }
                    else if (errorMessage.includes('Holiday with overlapping dates already exists')) {
                        return { errors: { name: errorMessage } };
                    }
                    return false;
                }
            }
            else {
                const res = await ApiCall('POST', '/api/holidays', payload, {
                    Authorization: token,
                });
                if (res && 'data' in res && res?.data?.success) {
                    toast.success('Holiday created successfully');
                    setDrawerOpen(false);
                    fetchHolidays();
                    return true;
                }
                else {
                    const errorRes = res;
                    const errorMessage = errorRes?.response?.data?.message || 'Create failed';
                    toast.error(errorMessage);
                    // Return error object for formik to display
                    if (errorMessage.includes('End date cannot be before start date')) {
                        return { errors: { end_date: errorMessage } };
                    }
                    else if (errorMessage.includes('Previous end date cannot be before previous start date')) {
                        return { errors: { prev_end_date: errorMessage } };
                    }
                    else if (errorMessage.includes('Current year and previous year cannot be the same')) {
                        return { errors: { prev_start_date: errorMessage } };
                    }
                    else if (errorMessage.includes('Holiday with overlapping dates already exists')) {
                        return { errors: { name: errorMessage } };
                    }
                    return false;
                }
            }
        }
        catch (error) {
            toast.error('An unexpected error occurred. Please try again.');
            return false;
        }
    };
    const columns = [
        {
            id: 'created_at',
            label: 'Date',
            width: '100px',
            align: 'left',
            sortable: true,
        },
        {
            id: 'name',
            label: 'Holiday Name',
            width: '180px',
            align: 'left',
            sortable: true,
        },
        {
            id: 'cur_promotion_date_range',
            label: 'Current Date Range',
            width: '180px',
            align: 'left',
            sortable: true,
        },
        {
            id: 'prev_promotion_date_range',
            label: 'Previous Date Range',
            width: '180px',
            align: 'left',
            sortable: true,
        },
        {
            id: 'actions',
            label: 'Actions',
            width: '128px',
            align: 'center',
            sortable: false,
            draggable: false,
            showColumnActions: false,
        },
    ];
    const renderCell = (row, column) => {
        const rowIndex = rows.findIndex((h) => h.id === row.id);
        const displayIndex = (currentPage - 1) * itemsPerPage + rowIndex + 1;
        const roleClass = roleClassSequence[rowIndex % roleClassSequence.length];
        switch (column.id) {
            case 'index':
                return (_jsx(Typography, { className: "user-table-index-text", children: displayIndex }));
            case 'created_at':
                return (_jsx(Typography, { className: "user-table-email-text", children: row.created_at }));
            case 'name':
                return (_jsx(Box, { className: "user-table-name-cell", children: _jsx(Box, { className: "user-table-name-wrapper", children: _jsx(Typography, { className: "user-table-name-text", children: row.name }) }) }));
            case 'cur_promotion_date_range':
                return (_jsxs(Typography, { className: "user-table-email-text", children: [row.start_date, " - ", row.end_date] }));
            case 'prev_promotion_date_range':
                const prevDateRange = row.prev_start_date && row.prev_end_date
                    ? `${row.prev_start_date} - ${row.prev_end_date}`
                    : '-';
                return (_jsx(Typography, { className: "user-table-email-text", children: prevDateRange }));
            case 'actions':
                return (_jsx(Box, { className: "user-table-actions-cell", children: hasPermission && (_jsxs(_Fragment, { children: [editLoadingId === row.id ? (_jsx(CircularProgress, { color: "primary", size: 18 })) : (_jsx(IconButton, { className: "user-table-action-button", onClick: () => handleEdit(row), size: "small", children: _jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M14.167 2.5A2.357 2.357 0 0 1 17.5 5.833L6.25 17.083l-4.583 1.25 1.25-4.583z", stroke: "#52525b", strokeWidth: "1.2", strokeLinecap: "round", strokeLinejoin: "round" }) }) })), _jsx(IconButton, { className: "user-table-action-button user-delete-btn", onClick: () => handleDelete(row), size: "small", children: _jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M2.5 5h1.667m0 0H17.5M4.167 5v11.667a1.667 1.667 0 0 0 1.666 1.667h8.334a1.667 1.667 0 0 0 1.666-1.667V5zm2.5 0V3.334a1.667 1.667 0 0 1 1.666-1.667h3.334a1.667 1.667 0 0 1 1.666 1.667V5m-5 4.167v5m3.334-5v5", stroke: "#535862", strokeWidth: "1.2", strokeLinecap: "round", strokeLinejoin: "round" }) }) })] })) }));
            default:
                return null;
        }
    };
    const columnFilterConfig = useMemo(() => ({
        enable: true,
        columns: {
            name: {
                type: 'search',
            },
        },
    }), []);
    return (_jsxs(PageContainer, { children: [_jsx(Box, { className: "user-list-container holiday-calendar-module", children: _jsxs(Paper, { className: "user-list-card", elevation: 0, children: [_jsx(Box, { className: "user-list-header", children: _jsxs(Box, { className: "user-list-header-content", children: [_jsx(CommonTabs, { selectedValue: activeTab, onChange: (val) => { setActiveTab(val); setCurrentPage(1); }, tabs: [
                                            {
                                                value: 'active',
                                                label: (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx(Box, { className: "user-list-tab-dot user-list-tab-dot-active", sx: { margin: 0 } }), "Active (", activeCount, ")"] }))
                                            },
                                            {
                                                value: 'inactive',
                                                label: (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx(Box, { className: "user-list-tab-dot user-list-tab-dot-inactive", sx: { margin: 0 } }), "Inactive (", inactiveCount, ")"] }))
                                            }
                                        ] }), _jsxs(Box, { className: "user-list-toolbar-actions ", children: [_jsx(TableToolbar, { title: "", showSearch: true, showRows: true, showExpand: true, showFullScreen: true, onSearchChange: handleSearchChange, searchValue: searchText, enableColumnVisibility: true, onColumnVisibilityClick: handleColumnVisibilityClick, enableColumnFilters: false, showFilterRow: showFilterRow, onFilterRowToggle: () => {
                                                    setShowFilterRow((prev) => {
                                                        const next = !prev;
                                                        if (!next) {
                                                            setActiveFilterColumnId(null);
                                                        }
                                                        return next;
                                                    });
                                                } }), hasPermission && (_jsx(AppButton, { variant: "contained", color: "primary", className: "app-common-button", onClick: handleAddNewHoliday, startIcon: _jsx(AddIcon, {}), children: "Add Holiday" }))] })] }) }), _jsx(Box, { className: "user-list-table-wrapper ", children: _jsx(CommonTable, { columns: columns, rows: rows, renderCell: renderCell, sortConfig: sortConfig, onSortChange: handleSortChange, expandTrigger: "none", type: "userlist-table", isLoading: loading, isFirstLoad: true, enableSelection: false, selectedRows: selectedRows, onSelectionChange: handleSelectionChange, columnVisibilityConfig: {
                                    enable: true,
                                    columns: [
                                        'created_at',
                                        'cur_promotion_date_range',
                                        'prev_promotion_date_range',
                                        'name',
                                        'actions',
                                    ],
                                    enableReorder: true,
                                }, hiddenColumns: hiddenColumns, onColumnVisibilityChange: handleColumnVisibilityChange, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: handleColumnVisibilityClose, enableColumnActions: true, enableColumnFilters: false, showFilterRow: showFilterRow, activeFilterColumnId: activeFilterColumnId, onActiveFilterColumnIdChange: setActiveFilterColumnId, columnFilters: columnFilters, onColumnFilterChange: handleColumnFilterChange, onFilterRowToggle: handleFilterRowToggle, enableColumnReorder: true, columnOrder: columnOrder, onColumnOrderChange: handleColumnOrderChange, enableColumnPinning: true, pinnedColumns: pinnedColumns, onColumnPinningChange: handleColumnPinningChange, columnFilterConfig: columnFilterConfig }) }), totalPages > 1 && (_jsx(Box, { className: "user-list-pagination-wrapper", sx: {
                                paddingInline: '14px'
                            }, children: _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, onPageChange: handlePageChange, maxVisiblePages: 5, totalEntries: activeTab === 'active' ? activeCount : inactiveCount, itemsPerPage: itemsPerPage, showEntriesMessage: true }) }))] }) }), drawerOpen && (_jsx(HolidayDrawer, { open: drawerOpen, onClose: handleDrawerClose, holidayData: selectedHoliday, onSave: handleSaveHoliday, totalHolidaysCount: totalHolidaysCount })), deleteState?.open && (_jsx(ConfirmActionModal, { className: "delete-popup-container", open: deleteState.open, loading: deleteState.loading, title: "Are You Sure?", description: "You\u2019re about to remove this holiday record. Once deleted, this action cannot be undone.", confirmText: "Yes, Proceed", cancelText: "No, Cancel", onClose: () => !deleteState.loading &&
                    setDeleteState({ open: false, loading: false, payload: null }), onConfirm: confirmDelete }))] }));
}
